import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shell } from '../components/Shell';
import { TypedPassage } from '../components/TypedPassage';
import { AlertIcon, CheckIcon, RotateIcon } from '../components/Icons';
import { CodeState, draftKey, fetchCodeState, finishCode, formatTimeLeft, LocalTry, TryResult, useTimeLeft } from '../lib/tries';
import { Segmented } from '../components/Segmented';
import { AnimatedNumber } from '../components/AnimatedNumber';

type DisplayTry = TryResult & { unsaved?: boolean };

const readLocalTry = (code: string): LocalTry | null => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem('testResults') || 'null');
    if (!parsed || (parsed.identifier_code && parsed.identifier_code !== code)) return null;
    return { try_number: 1, ...parsed };
  } catch {
    return null;
  }
};

function Metric({ label, value, hint, primary }: { label: string; value: React.ReactNode; hint?: string; primary?: boolean }) {
  return (
    <div className={`metric${primary ? ' primary' : ''}`}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {hint && <div className="metric-hint">{hint}</div>}
    </div>
  );
}

const Results: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [server, setServer] = useState<CodeState | null>(null);
  const [local, setLocal] = useState<LocalTry | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState('');
  const timeLeft = useTimeLeft(server?.open ? server.expires_at : null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    const localTry = readLocalTry(code);
    setLocal(localTry);
    try {
      const state = await fetchCodeState();
      setServer(state);
      const latest = state.tries[state.tries.length - 1]?.try_number ?? null;
      setSelected(localTry?.try_number ?? latest);
    } catch {
      setLoadFailed(true);
      setSelected(localTry?.try_number ?? null);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    // Practice is scored on its own page and never stored
    if (code === 'PRACTICE') {
      navigate('/practice', { replace: true });
      return;
    }
    if (sessionStorage.getItem('userCode') !== code || !sessionStorage.getItem('userToken')) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [code, navigate, load]);

  // When the hour runs out the server submits their best try; reload to show that
  useEffect(() => {
    if (timeLeft === 0) {
      setConfirmOpen(false);
      load();
    }
  }, [timeLeft, load]);

  // Close the confirm dialog with Escape
  useEffect(() => {
    if (!confirmOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !finishing) setConfirmOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmOpen, finishing]);

  const handleFinish = async () => {
    setFinishing(true);
    setFinishError('');
    try {
      const state = await finishCode();
      localStorage.removeItem(draftKey(code));
      setServer(state);
      setConfirmOpen(false);
    } catch {
      setFinishError("Your results couldn't be submitted. Check your connection and try again.");
    } finally {
      setFinishing(false);
    }
  };

  const handleDone = () => {
    sessionStorage.removeItem('userCode');
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('testResults');
    navigate('/');
  };

  // Server tries, plus the just-finished try if the server never confirmed it
  const tries: DisplayTry[] = [...(server?.tries ?? [])];
  if (local && local._submitted === false && !tries.some(t => t.try_number === local.try_number)) {
    tries.push({ ...local, unsaved: true });
    tries.sort((a, b) => a.try_number - b.try_number);
  }
  const current = tries.find(t => t.try_number === selected) ?? tries[tries.length - 1];
  const bestTry = server?.best_try ?? null;
  const best = tries.find(t => t.try_number === bestTry);
  const maxTries = server?.max_tries ?? 3;
  const hasUnsaved = tries.some(t => t.unsaved);

  if (loading) {
    return (
      <Shell brandLink={false}>
        <div className="results">
          <div className="page-header">
            <h1>Test Results</h1>
          </div>
          <div className="metric-grid">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton" style={{ height: 100 }} />)}
          </div>
          <div className="skeleton" style={{ height: 180, marginTop: 58 }} />
        </div>
      </Shell>
    );
  }

  if (!current) {
    return (
      <Shell>
        <div className="card entry-card" style={{ maxWidth: 440, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: 10 }}>
            {loadFailed ? "Couldn't Load Your Results" : 'No Results Found'}
          </h2>
          <p style={{ color: 'var(--ink-2)', lineHeight: 1.6, fontSize: '0.95rem' }}>
            {loadFailed ? 'We couldn\'t reach the server. Check your internet connection and try again.' : 'There are no results for this code yet.'}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
            {loadFailed && <button type="button" className="btn btn-primary" onClick={load}>Try Again</button>}
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>Go Home</button>
          </div>
        </div>
      </Shell>
    );
  }

  const triesUsed = server?.tries.length ?? tries.length;
  const triesLeft = maxTries - triesUsed;

  return (
    <Shell brandLink={false}>
      <div className="results">
        <div className="page-header" style={{ marginBottom: 20 }}>
          <h1>Test Results</h1>
          <div className="page-sub">
            <span className="chip">Code <strong>{code}</strong></span>
            <span className="chip">{triesUsed} of {maxTries} {maxTries === 1 ? 'Try' : 'Tries'} Used</span>
          </div>
        </div>

        {tries.length > 1 && (
          <div className="try-tabs">
            <Segmented
              kind="tab"
              ariaLabel="Tries"
              value={current.try_number}
              onChange={setSelected}
              options={tries.map(t => ({
                value: t.try_number,
                label: (
                  <>
                    Try {t.try_number}
                    <span className="tab-score">{t.adjusted_wpm} WPM</span>
                    {t.try_number === bestTry && <span className="best-badge">Best</span>}
                  </>
                ),
              }))}
            />
          </div>
        )}

        <div className="metric-grid">
          <Metric label="Net WPM" value={<AnimatedNumber value={current.adjusted_wpm} />} hint="Gross × accuracy" primary />
          <Metric label="Gross WPM" value={<AnimatedNumber value={current.raw_wpm} />} hint="Everything typed" />
          <Metric label="Accuracy" value={<AnimatedNumber value={current.accuracy} suffix="%" />} hint="Correct characters" />
          <Metric label="Consistency" value={<AnimatedNumber value={current.consistency} suffix="%" />} hint="Steadiness of rhythm" />
          <Metric label="Errors/Min" value={<AnimatedNumber value={Number(current.errors_per_minute)} digits={1} />} hint="Mistakes per minute" />
          <Metric label="Duration" value={<AnimatedNumber value={Math.min(60, Math.round(Number(current.test_duration)))} suffix="s" />} hint="Test length" />
        </div>

        <div className="section-title">
          <h2>What You Typed{tries.length > 1 ? ` (Try ${current.try_number})` : ''}</h2>
          <span>Mistakes are highlighted</span>
        </div>
        <TypedPassage typed={current.typed_text} original={current.original_text} />

        {hasUnsaved && (
          <div className="notice danger" style={{ marginTop: 16 }}>
            <AlertIcon size={16} />
            <span>
              Try {tries.find(t => t.unsaved)?.try_number} couldn't be saved to the server yet. Select Retry Saving to send it again,
              or contact your administrator if this keeps happening.
            </span>
          </div>
        )}
        {loadFailed && !hasUnsaved && (
          <div className="notice danger" style={{ marginTop: 16 }}>
            <AlertIcon size={16} />
            <span>We couldn't reach the server to check your tries. <button type="button" className="btn btn-ghost" style={{ height: 'auto', padding: 0, color: 'inherit', textDecoration: 'underline' }} onClick={load}>Try again</button></span>
          </div>
        )}

        {server && (
          <div className="card results-actions">
            {server.finished ? (
              <>
                <div className="submitted">
                  <div className="icon-badge"><CheckIcon size={20} /></div>
                  <p>
                    <strong>Your results have been submitted.</strong> Your best score,{' '}
                    <strong>{best?.adjusted_wpm ?? 0} Net WPM</strong>{triesUsed > 1 ? ` from Try ${bestTry}` : ''}, is the one that counts. Thank you!
                  </p>
                </div>
                <div className="buttons">
                  <button type="button" className="btn btn-primary" onClick={handleDone}>Done</button>
                </div>
              </>
            ) : hasUnsaved ? (
              <>
                <p>Your last try needs to be saved before you can continue.</p>
                <div className="buttons">
                  <button type="button" className="btn btn-primary" onClick={() => navigate(`/test/${code}`)}>
                    <RotateIcon size={16} />
                    Retry Saving
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>
                  Your best score so far is <strong>{best?.adjusted_wpm ?? 0} Net WPM</strong>{triesUsed > 1 ? ` (Try ${bestTry})` : ''}.{' '}
                  You have <strong>{triesLeft} {triesLeft === 1 ? 'try' : 'tries'} left</strong>
                  {timeLeft !== null && <> and <strong>{formatTimeLeft(timeLeft)}</strong> to use {triesLeft === 1 ? 'it' : 'them'}</>}.
                  Retake the test to try to beat your score, or finish to submit it. If time runs out, your best score is submitted automatically.
                </p>
                <div className="buttons">
                  <button type="button" className="btn btn-secondary" onClick={() => setConfirmOpen(true)}>
                    Finish &amp; Submit
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => navigate(`/test/${code}`)}>
                    <RotateIcon size={16} />
                    Retake Test
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {confirmOpen && (
        <div className="dialog-backdrop" onMouseDown={() => !finishing && setConfirmOpen(false)}>
          <div className="card dialog" role="dialog" aria-modal="true" aria-labelledby="finish-title" onMouseDown={e => e.stopPropagation()}>
            <h2 id="finish-title">Finish and Submit?</h2>
            <p>
              Your best score, <strong style={{ color: 'var(--ink)' }}>{best?.adjusted_wpm ?? 0} Net WPM</strong>
              {triesUsed > 1 ? ` from Try ${bestTry}` : ''}, will be submitted. You won't be able to take any more tries with this code.
            </p>
            {finishError && <div className="entry-error" style={{ textAlign: 'left' }}>{finishError}</div>}
            <div className="buttons">
              <button type="button" className="btn btn-ghost" disabled={finishing} onClick={() => setConfirmOpen(false)}>
                Keep Testing
              </button>
              <button type="button" className="btn btn-primary" disabled={finishing} onClick={handleFinish} autoFocus>
                {finishing ? <span className="loading" style={{ width: 16, height: 16, borderWidth: 2 }} /> : 'Finish & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
};

export default Results;
